import styled, { css, keyframes } from 'styled-components';
import { ThemeInterface } from '../../interfaces/themeInterface';

const getOppositeBackground = (theme: ThemeInterface) =>
  theme.name === 'long' ? 'blue' : '#ffccd5';

const getOppositeText = (theme: ThemeInterface) => (theme.name === 'long' ? 'white' : 'black');

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow: hidden;
  z-index: 2000;
`;

const backgroundDissolve = keyframes`
  0% {
    opacity: 1;
  }
  18% {
    opacity: 0.92;
  }
  42% {
    opacity: 0.76;
  }
  65% {
    opacity: 0.5;
  }
  84% {
    opacity: 0.2;
  }
  100% {
    opacity: 0;
  }
`;

export const BackgroundOnlyOverlay = styled(Overlay)<{
  $visible: boolean;
  $exiting: boolean;
}>`
  pointer-events: none;
  z-index: 1000;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  animation: ${({ $exiting }) =>
    $exiting
      ? css`
          ${backgroundDissolve} 420ms linear forwards
        `
      : 'none'};
`;

export const BackgroundLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
`;

export const BackgroundCanvas = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
  filter: none;
`;

export const BackgroundVeil = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: ${({ theme }) =>
    theme.name === 'long'
      ? 'linear-gradient(180deg, rgba(255, 204, 213, 0.16) 0%, rgba(255, 204, 213, 0.24) 100%)'
      : 'linear-gradient(180deg, rgba(0, 0, 255, 0.16) 0%, rgba(0, 0, 255, 0.24) 100%)'};
`;

export const Card = styled.div`
  background: ${({ theme }) => getOppositeBackground(theme)};
  color: ${({ theme }) => getOppositeText(theme)};
  border: 1px solid ${({ theme }) => theme.primary};
  padding: 1.5rem;
  max-width: 360px;
  width: 90%;
  font-family: monospace;
  position: relative;
  z-index: 3;
  box-shadow: 0 30px 100px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(1px);
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: none;
  border: none;
  color: inherit;
  font-family: monospace;
  font-size: 0.65rem;
  cursor: pointer;
  opacity: 0.5;
  padding: 0.25rem;

  &:hover {
    opacity: 1;
  }
`;

export const Title = styled.h2`
  font-size: 0.85rem;
  letter-spacing: 0.1em;
  margin: 0 0 0.5rem 0;
  opacity: 0.9;
`;

export const Description = styled.p`
  font-size: 0.75rem;
  opacity: 0.7;
  line-height: 1.5;
  margin: 0 0 1.25rem 0;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const Input = styled.input`
  background: transparent;
  border: 1px solid currentColor;
  padding: 0.6rem 0.75rem;
  font-size: 0.75rem;
  font-family: monospace;
  color: inherit;
  outline: none;
  opacity: 0.8;

  &:focus {
    opacity: 1;
  }

  &::placeholder {
    color: inherit;
    opacity: 0.4;
  }
`;

export const Button = styled.button`
  background: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.background};
  border: none;
  padding: 0.6rem 0.75rem;
  font-size: 0.7rem;
  font-family: monospace;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const SecondaryButton = styled.button`
  background: transparent;
  border: 1px solid currentColor;
  color: inherit;
  padding: 0.6rem 0.75rem;
  font-size: 0.7rem;
  font-family: monospace;
  cursor: pointer;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`;

export const Message = styled.p<{ $type?: 'success' | 'error' }>`
  font-size: 0.7rem;
  opacity: ${({ $type }) => ($type ? 1 : 0.6)};
  color: ${({ $type }) =>
    $type === 'success' ? '#22c55e' : $type === 'error' ? '#ef4444' : 'inherit'};
  margin: 0;
`;

export const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1rem 0;
  opacity: 0.4;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: currentColor;
  }

  span {
    font-size: 0.65rem;
  }
`;
